import { definePreset } from '@primeng/themes';
// import Aura from '@primeng/themes/aura';
import Aura from '@primeng/themes/aura';

export const j4g = definePreset(Aura, {
  extend: {
    shadowHeight: '0.5rem',
    borderRadius: {
      xxl: '24px',
    },
    malachite: {
      50: '#f0ffe5',
      100: '#dcffc6',
      200: '#baff93',
      300: '#8cff55',
      400: '#64f823',
      500: '#42dd03',
      600: '#30b300',
      700: '#268704',
      800: '#226a0a',
      900: '#1f5a0d',
      950: '#0b3201',
    },
    elephant: {
      50: '#effcfc',
      100: '#d5f4f8',
      200: '#b1e9f0',
      300: '#7bd7e5',
      400: '#3ebbd2',
      500: '#229fb8',
      600: '#1f819b',
      700: '#20687e',
      800: '#225668',
      900: '#1a3f4c',
      950: '#102e3b',
    },
    aquamarine: {
      50: '#f0fdf6',
      100: '#ddfbec',
      200: '#bcf6da',
      300: '#88edbe',
      400: '#4ddb99',
      500: '#29d686',
      600: '#19a062',
      700: '#177e4f',
      800: '#186341',
      900: '#155237',
      950: '#062d1d',
    },
    turquoise: {
      50: '#effefb',
      100: '#c9fef7',
      200: '#92fdf1',
      300: '#54f4e8',
      400: '#1ed6ce',
      500: '#08c4be',
      600: '#039e9d',
      700: '#087c7d',
      800: '#0c6163',
      900: '#0f5052',
      950: '#012f32',
    },
    'smalt-blue': {
      50: '#f3f8f8',
      100: '#e0ebed',
      200: '#c5d9dc',
      300: '#9cbcc4',
      400: '#6c99a4',
      500: '#598a97',
      600: '#466874',
      700: '#3d5761',
      800: '#384a52',
      900: '#324047',
      950: '#1e292e',
    },
    formField: {
      xxs: {
        fontSize: '0.625rem',
        paddingX: '0.375rem',
        paddingY: '0.25rem',
      },
      xs: {
        fontSize: '0.75rem',
        paddingX: '0.5rem',
        paddingY: '0.375rem',
      },
      xl: {
        fontSize: '1.25rem',
        paddingX: '1rem',
        paddingY: '0.875rem',
      },
      xxl: {
        fontSize: '1.375rem',
        paddingX: '1.125rem',
        paddingY: '1rem',
      },
      xxxl: {
        fontSize: '2.5rem',
        paddingX: '1.25rem',
        paddingY: '1.125rem',
      },
    },
    colorScheme: {
      light: {
        accent: {
          color: '{elephant.950}',
          contrastColor: '{primary.500}',
          hoverColor: '{elephant.800}',
          activeColor: '{elephant.900}',
        },
        'accent-light': {
          color: '{elephant.800}',
          contrastColor: '{surface.0}',
          hoverColor: '{elephant.600}',
          activeColor: '{elephant.700}',
        },
        'accent-muted': {
          color: '{elephant.950}',
          contrastColor: '{surface.100}',
          hoverColor: '{elephant.800}',
          activeColor: '{elephant.900}',
        },
        aquamarine: {
          color: '{aquamarine.500}',
          contrastColor: '{surface.0}',
          hoverColor: '{aquamarine.600}',
          activeColor: '{aquamarine.700}',
        },
        turquoise: {
          color: '{turquoise.400}',
          contrastColor: '{surface.0}',
          hoverColor: '{turquoise.500}',
          activeColor: '{turquoise.600}',
        },
        'smalt-blue': {
          color: '{smalt-blue.500}',
          contrastColor: '{surface.0}',
          hoverColor: '{smalt-blue.600}',
          activeColor: '{smalt-blue.700}',
        },
      },
    },
  },
  primitive: {
    slate: {
      50: '#fbfbfb',
      100: '#ededed',
      200: '#dedede',
      300: '#d0d0d0',
      400: '#c1c1c1',
      500: '#b3b3b3',
      600: '#989898',
      700: '#7d7d7d',
      800: '#626262',
      900: '#484848',
      950: '#2d2d2d',
    },
  },
  semantic: {
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
    },
    primary: {
      50: '{malachite.50}',
      100: '{malachite.100}',
      200: '{malachite.200}',
      300: '{malachite.300}',
      400: '{malachite.400}',
      500: '{malachite.500}',
      600: '{malachite.600}',
      700: '{malachite.700}',
      800: '{malachite.800}',
      900: '{malachite.900}',
      950: '{malachite.950}',
    },
    formField: {
      sm: {
        paddingY: '0.5rem',
      },
      paddingY: '0.625rem',
      lg: {
        paddingY: '0.75rem',
      },
      borderRadius: '{border.radius.lg}',
      focusRing: {
        width: '{focus.ring.width}',
        style: '{focus.ring.style}',
        color: '{focus.ring.color}',
        offset: '{focus.ring.offset}',
        shadow: '{focus.ring.shadow}',
      },
    },
    list: {
      padding: '0.5rem 0',
      gap: '0',
      header: {
        padding: '0.625rem 1rem 0 1rem',
      },
      option: {
        padding: '0.625rem 1rem',
        borderRadius: '0',
      },
      optionGroup: {
        padding: '0.625rem 1rem',
        fontWeight: '600',
      },
    },
    navigation: {
      list: {
        padding: '0.5rem 0',
        gap: '0',
      },
      item: {
        padding: '0.625rem 1rem',
        borderRadius: '0',
      },
      submenuLabel: {
        padding: '0.625rem 1rem',
      },
    },
    overlay: {
      select: {
        shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
      },
      popover: {
        padding: '1rem',
        shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
      modal: {
        padding: '1.5rem',
        shadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      },
      navigation: {
        shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
      },
    },
    colorScheme: {
      light: {
        primary: {
          contrastColor: '{elephant.950}',
        },
        formField: {
          borderColor: '{primary.300}',
          hoverBorderColor: '{primary.400}',
          focusBorderColor: '{primary.color}',
          iconColor: '{primary.color}',
        },
      },
    },
  },
  components: {
    button: {
      css: () => `
        .p-button-xxs {
          font-size: var(--p-button-xxs-font-size);
          padding: var(--p-button-xxs-padding-y) var(--p-button-xxs-padding-x);
        }
        .p-button-xs {
          font-size: var(--p-button-xs-font-size);
          padding: var(--p-button-xs-padding-y) var(--p-button-xs-padding-x);
        }
        .p-button-xl {
          font-size: var(--p-button-xl-font-size);
          padding: var(--p-button-xl-padding-y) var(--p-button-xl-padding-x);
        }
        .p-button-xxl {
          font-size: var(--p-button-xxl-font-size);
          padding: var(--p-button-xxl-padding-y) var(--p-button-xxl-padding-x);
        }
        .p-button-xxxl {
          font-size: var(--p-button-xxxl-font-size);
          padding: var(--p-button-xxxl-padding-y) var(--p-button-xxxl-padding-x);
        }
        .p-button-xxs.p-button-icon-only {
          width: var(--p-button-xxs-icon-only-width);
          height: var(--p-button-xxs-icon-only-width);
        }
        .p-button-xs.p-button-icon-only {
          width: var(--p-button-xs-icon-only-width);
          height: var(--p-button-xs-icon-only-width);
        }
        .p-button-sm.p-button-icon-only {
          width: var(--p-button-sm-icon-only-width);
          height: var(--p-button-sm-icon-only-width);
        }
        .p-button-icon-only {
          width: var(--p-button-icon-only-width);
          height: var(--p-button-icon-only-width);
        }
        .p-button-lg.p-button-icon-only {
          width: var(--p-button-lg-icon-only-width);
          height: var(--p-button-lg-icon-only-width);
        }
        .p-button-xl.p-button-icon-only {
          width: var(--p-button-xl-icon-only-width);
          height: var(--p-button-xl-icon-only-width);
        }
        .p-button-xxl.p-button-icon-only {
          width: var(--p-button-xxl-icon-only-width);
          height: var(--p-button-xxl-icon-only-width);
        }
        .p-button-xxxl.p-button-icon-only {
          width: var(--p-button-xxxl-icon-only-width);
          height: var(--p-button-xxxl-icon-only-width);
        }
        p-button[effect="false"] > .p-button > .p-ink {
          background: transparent!important;
        }
        p-button[effect="false"] > .p-button:active {
          background: transparent!important;
        }
        p-button[effect="false"] > .p-button:hover {
          background: transparent!important;
        }
        p-button:not([effect="false"]) > .p-button-outlined.p-button-primary:not(:disabled):active, p-button:not([effect="false"]) > .p-button-text.p-button-primary:not(:disabled):active {
          color: var(--p-button-primary-active-color)!important;
        }
        p-button:not([effect="false"]) > .p-button-outlined.p-button-primary:not(:disabled):hover, p-button:not([effect="false"]) > .p-button-text.p-button-primary:not(:disabled):hover {
          color: var(--p-button-primary-hover-color)!important;
        }
        .p-button-text.p-button-accent{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-accent-color);
        }
        .p-button-text.p-button-accent:not(:disabled):active {
          background: var(--p-button-text-accent-active-background);
          border-color: transparent;
          color: var(--p-button-text-accent-color);
        }
        .p-button-text.p-button-accent:not(:disabled):hover {
          background: var(--p-button-text-accent-hover-background);
          border-color: transparent;
          color: var(--p-button-text-accent-color);
        }
       
        .p-button-accent {
          background: var(--p-button-accent-background);
          color: var(--p-button-accent-color);
          border-color: var(--p-button-accent-border-color);
        }
        .p-button-accent:not(:disabled):active {
          background: var(--p-button-accent-active-background);
          color: var(--p-button-accent-active-color);
          border-color: var(--p-button-accent-active-border-color);
        }
        .p-button-accent:not(:disabled):hover {
          background: var(--p-button-accent-hover-background);
          color: var(--p-button-accent-hover-color);
          border-color: var(--p-button-accent-hover-border-color);
        }
        .p-button-accent:focus-visible {
          outline-color: var(--p-button-accent-focus-ring-color);
          box-shadow: var(--p-button-accent-focus-ring-shadow);
        }
        .p-button-text.p-button-accent-light{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-accent-light-color);
        }
        .p-button-text.p-button-accent-light:not(:disabled):active {
          background: var(--p-button-text-accent-light-active-background);
          border-color: transparent;
          color: var(--p-button-text-accent-light-color);
        }
        .p-button-text.p-button-accent-light:not(:disabled):hover {
          background: var(--p-button-text-accent-light-hover-background);
          border-color: transparent;
          color: var(--p-button-text-accent-light-color);
        }

         
        .p-button-accent-light {
          background: var(--p-button-accent-light-background);
          color: var(--p-button-accent-light-color);
          border-color: var(--p-button-accent-light-border-color);
        }
        .p-button-accent-light:not(:disabled):active {
          background: var(--p-button-accent-light-active-background);
          color: var(--p-button-accent-light-active-color);
          border-color: var(--p-button-accent-light-active-border-color);
        }
        .p-button-accent-light:not(:disabled):hover {
          background: var(--p-button-accent-light-hover-background);
          color: var(--p-button-accent-light-hover-color);
          border-color: var(--p-button-accent-light-hover-border-color);
        }
        .p-button-accent-light:focus-visible {
          outline-color: var(--p-button-accent-light-focus-ring-color);
          box-shadow: var(--p-button-accent-light-focus-ring-shadow);
        }
        .p-button-text.p-button-accent-muted{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-accent-muted-color);
        }
        .p-button-text.p-button-accent-muted:not(:disabled):active {
          background: var(--p-button-text-accent-muted-active-background);
          border-color: transparent;
          color: var(--p-button-text-accent-muted-color);
        }
        .p-button-text.p-button-accent-muted:not(:disabled):hover {
          background: var(--p-button-text-accent-muted-hover-background);
          border-color: transparent;
          color: var(--p-button-text-accent-muted-color);
        }

        
        .p-button-accent-muted {
          background: var(--p-button-accent-muted-background);
          color: var(--p-button-accent-muted-color);
          border-color: var(--p-button-accent-muted-border-color);
        }
        .p-button-accent-muted:not(:disabled):active {
          background: var(--p-button-accent-muted-active-background);
          color: var(--p-button-accent-muted-active-color);
          border-color: var(--p-button-accent-muted-active-border-color);
        }
        .p-button-accent-muted:not(:disabled):hover {
          background: var(--p-button-accent-muted-hover-background);
          color: var(--p-button-accent-muted-hover-color);
          border-color: var(--p-button-accent-muted-hover-border-color);
        }
        .p-button-accent-muted:focus-visible {
          outline-color: var(--p-button-accent-muted-focus-ring-color);
          box-shadow: var(--p-button-accent-muted-focus-ring-shadow);
        }
        .p-button-text.p-button-aquamarine{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-aquamarine-color);
        }
        .p-button-text.p-button-aquamarine:not(:disabled):active {
          background: var(--p-button-text-aquamarine-active-background);
          border-color: transparent;
          color: var(--p-button-text-aquamarine-color);
        }
        .p-button-text.p-button-aquamarine:not(:disabled):hover {
          background: var(--p-button-text-aquamarine-hover-background);
          border-color: transparent;
          color: var(--p-button-text-aquamarine-color);
        }

        .p-button-aquamarine {
          background: var(--p-button-aquamarine-background);
          color: var(--p-button-aquamarine-color);
          border-color: var(--p-button-aquamarine-border-color);
        }
        .p-button-aquamarine:not(:disabled):active {
          background: var(--p-button-aquamarine-active-background);
          color: var(--p-button-aquamarine-active-color);
          border-color: var(--p-button-aquamarine-active-border-color);
        }
        .p-button-aquamarine:not(:disabled):hover {
          background: var(--p-button-aquamarine-hover-background);
          color: var(--p-button-aquamarine-hover-color);
          border-color: var(--p-button-aquamarine-hover-border-color);
        }
        .p-button-aquamarine:focus-visible {
          outline-color: var(--p-button-aquamarine-focus-ring-color);
          box-shadow: var(--p-button-aquamarine-focus-ring-shadow);
        }
        .p-button-text.p-button-turquoise{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-turquoise-color);
        }
        .p-button-text.p-button-turquoise:not(:disabled):active {
          background: var(--p-button-text-turquoise-active-background);
          border-color: transparent;
          color: var(--p-button-text-turquoise-color);
        }
        .p-button-text.p-button-turquoise:not(:disabled):hover {
          background: var(--p-button-text-turquoise-hover-background);
          border-color: transparent;
          color: var(--p-button-text-turquoise-color);
        }



        .p-button-turquoise {
          background: var(--p-button-turquoise-background);
          color: var(--p-button-turquoise-color);
          border-color: var(--p-button-turquoise-border-color);
        }
        .p-button-turquoise:not(:disabled):active {
          background: var(--p-button-turquoise-active-background);
          color: var(--p-button-turquoise-active-color);
          border-color: var(--p-button-turquoise-active-border-color);
        }
        .p-button-turquoise:not(:disabled):hover {
          background: var(--p-button-turquoise-hover-background);
          color: var(--p-button-turquoise-hover-color);
          border-color: var(--p-button-turquoise-hover-border-color);
        }
        .p-button-turquoise:focus-visible {
          outline-color: var(--p-button-turquoise-focus-ring-color);
          box-shadow: var(--p-button-turquoise-focus-ring-shadow);
        }
        .p-button-text.p-button-smalt-blue{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-smalt-blue-color);
        }
        .p-button-text.p-button-smalt-blue:not(:disabled):active {
          background: var(--p-button-text-smalt-blue-active-background);
          border-color: transparent;
          color: var(--p-button-text-smalt-blue-color);
        }
        .p-button-text.p-button-smalt-blue:not(:disabled):hover {
          background: var(--p-button-text-smalt-blue-hover-background);
          border-color: transparent;
          color: var(--p-button-text-smalt-blue-color);
        }


        .p-button-smalt-blue {
          background: var(--p-button-smalt-blue-background);
          color: var(--p-button-smalt-blue-color);
          border-color: var(--p-button-smalt-blue-border-color);
        }
        .p-button-smalt-blue:not(:disabled):active {
          background: var(--p-button-smalt-blue-active-background);
          color: var(--p-button-smalt-blue-active-color);
          border-color: var(--p-button-smalt-blue-active-border-color);
        }
        .p-button-smalt-blue:not(:disabled):hover {
          background: var(--p-button-smalt-blue-hover-background);
          color: var(--p-button-smalt-blue-hover-color);
          border-color: var(--p-button-smalt-blue-hover-border-color);
        }
        .p-button-smalt-blue:focus-visible {
          outline-color: var(--p-button-smalt-blue-focus-ring-color);
          box-shadow: var(--p-button-smalt-blue-focus-ring-shadow);
        }
        .p-button-text.p-button-white{
          background: transparent;
          border-color: transparent;
          color: var(--p-button-text-white-color);
        }
        .p-button-text.p-button-white:not(:disabled):active {
          background: var(--p-button-text-white-active-background);
          border-color: transparent;
          color: var(--p-button-text-white-color);
        }
        .p-button-text.p-button-white:not(:disabled):hover {
          background: var(--p-button-text-white-hover-background);
          border-color: transparent;
          color: var(--p-button-text-white-color);
        }


        .p-button-white {
          background: var(--p-button-white-background);
          color: var(--p-button-white-color);
          border-color: var(--p-button-white-border-color);
        }
        .p-button-white:not(:disabled):active {
          background: var(--p-button-white-active-background);
          color: var(--p-button-white-active-color);
          border-color: var(--p-button-white-active-border-color);
        }
        .p-button-white:not(:disabled):hover {
          background: var(--p-button-white-hover-background);
          color: var(--p-button-white-hover-color);
          border-color: var(--p-button-white-hover-border-color);
        }
        .p-button-white:focus-visible {
          outline-color: var(--p-button-white-focus-ring-color);
          box-shadow: var(--p-button-white-focus-ring-shadow);
        }


        p-button[glass] > .p-button-primary, p-button[glass] > .p-button{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-primary-background) 50%, var(--p-button-primary-background) 50%);
        }
        p-button[glass] > .p-button-primary:not(:disabled):active, p-button[glass] > .p-button:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-primary-active-background) 50%, var(--p-button-primary-active-background) 50%);
        }
        p-button[glass] > .p-button-primary:not(:disabled):hover, p-button[glass] > .p-button:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-primary-hover-background) 50%, var(--p-button-primary-hover-background) 50%);
        }
        p-button[glass] > .p-button-accent{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-background) 50%, var(--p-button-accent-background) 50%);
        }
        p-button[glass] > .p-button-accent:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-active-background) 50%, var(--p-button-accent-active-background) 50%);
        }
        p-button[glass] > .p-button-accent:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-hover-background) 50%, var(--p-button-accent-hover-background) 50%);
        }
        p-button[glass] > .p-button-aquamarine{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-aquamarine-background) 50%, var(--p-button-aquamarine-background) 50%);
        }
        p-button[glass] > .p-button-aquamarine:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-aquamarine-active-background) 50%, var(--p-button-aquamarine-active-background) 50%);
        }
        p-button[glass] > .p-button-aquamarine:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-aquamarine-hover-background) 50%, var(--p-button-aquamarine-hover-background) 50%);
        }
          
        p-button[glass] > .p-button-turquoise{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-turquoise-background) 50%, var(--p-button-turquoise-background) 50%);
        }
        p-button[glass] > .p-button-turquoise:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-turquoise-active-background) 50%, var(--p-button-turquoise-active-background) 50%);
        }
        p-button[glass] > .p-button-turquoise:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-turquoise-hover-background) 50%, var(--p-button-turquoise-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-smalt-blue{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-smalt-blue-background) 50%, var(--p-button-smalt-blue-background) 50%);
        }
        p-button[glass] > .p-button-smalt-blue:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-smalt-blue-active-background) 50%, var(--p-button-smalt-blue-active-background) 50%);
        }
        p-button[glass] > .p-button-smalt-blue:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-smalt-blue-hover-background) 50%, var(--p-button-smalt-blue-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-secondary{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-secondary-background) 50%, var(--p-button-secondary-background) 50%);
        }
        p-button[glass] > .p-button-secondary:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-secondary-active-background) 50%, var(--p-button-secondary-active-background) 50%);
        }
        p-button[glass] > .p-button-secondary:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-secondary-hover-background) 50%, var(--p-button-secondary-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-success{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-success-background) 50%, var(--p-button-success-background) 50%);
        }
        p-button[glass] > .p-button-success:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-success-active-background) 50%, var(--p-button-success-active-background) 50%);
        }
        p-button[glass] > .p-button-success:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-success-hover-background) 50%, var(--p-button-success-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-info{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-info-background) 50%, var(--p-button-info-background) 50%);
        }
        p-button[glass] > .p-button-info:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-info-active-background) 50%, var(--p-button-info-active-background) 50%);
        }
        p-button[glass] > .p-button-info:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-info-hover-background) 50%, var(--p-button-info-hover-background) 50%);
        }


        p-button[glass] > .p-button-warn{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-warn-background) 50%, var(--p-button-warn-background) 50%);
        }
        p-button[glass] > .p-button-warn:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-warn-active-background) 50%, var(--p-button-warn-active-background) 50%);
        }
        p-button[glass] > .p-button-warn:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-warn-hover-background) 50%, var(--p-button-warn-hover-background) 50%);
        }


        p-button[glass] > .p-button-help{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-help-background) 50%, var(--p-button-help-background) 50%);
        }
        p-button[glass] > .p-button-help:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-help-active-background) 50%, var(--p-button-help-active-background) 50%);
        }
        p-button[glass] > .p-button-help:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-help-hover-background) 50%, var(--p-button-help-hover-background) 50%);
        }


        p-button[glass] > .p-button-danger{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-danger-background) 50%, var(--p-button-danger-background) 50%);
        }
        p-button[glass] > .p-button-danger:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-danger-active-background) 50%, var(--p-button-danger-active-background) 50%);
        }
        p-button[glass] > .p-button-danger:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-danger-hover-background) 50%, var(--p-button-danger-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-contrast{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-contrast-background) 50%, var(--p-button-contrast-background) 50%);
        }
        p-button[glass] > .p-button-contrast:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-contrast-active-background) 50%, var(--p-button-contrast-active-background) 50%);
        }
        p-button[glass] > .p-button-contrast:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-contrast-hover-background) 50%, var(--p-button-contrast-hover-background) 50%);
        }



        p-button[glass] > .p-button-white{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-white-background) 50%, var(--p-button-white-background) 50%);
        }
        p-button[glass] > .p-button-white:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-white-active-background) 50%, var(--p-button-white-active-background) 50%);
        }
        p-button[glass] > .p-button-white:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-white-hover-background) 50%, var(--p-button-white-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-accent-light{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-light-background) 50%, var(--p-button-accent-light-background) 50%);
        }
        p-button[glass] > .p-button-accent-light:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-light-active-background) 50%, var(--p-button-accent-light-active-background) 50%);
        }
        p-button[glass] > .p-button-accent-light:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-light-hover-background) 50%, var(--p-button-accent-light-hover-background) 50%);
        }

        
        p-button[glass] > .p-button-accent-muted{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-muted-background) 50%, var(--p-button-accent-muted-background) 50%);
        }
        p-button[glass] > .p-button-accent-muted:not(:disabled):active {
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-muted-active-background) 50%, var(--p-button-accent-muted-active-background) 50%);
        }
        p-button[glass] > .p-button-accent-muted:not(:disabled):hover{
          background: radial-gradient(circle 5rem at center, transparent -50%, var(--p-button-accent-muted-hover-background) 50%, var(--p-button-accent-muted-hover-background) 50%);
        }

        p-button[inset-shadow] > .p-button{
          box-shadow: inset 0 0 10px var(--p-surface-300);
        }
        p-button[inset-shadow] > .p-button:not(:disabled):active {
        }
        p-button[inset-shadow] > .p-button:not(:disabled):hover{
        }

        p-button[inset-shadow] > .p-button-secondary{
          box-shadow: inset 0 0 10px var(--p-surface-100);
        }
        p-button[inset-shadow] > .p-button-white{
          box-shadow: inset 0 0 10px var(--p-surface-500);
        }

        p-button[inset-shadow][raised] > .p-button{
          box-shadow: inset 0 0 10px var(--p-surface-300), var(--p-button-raised-shadow);
        }
        p-button[inset-shadow][raised] > .p-button:not(:disabled):active {
        }
        p-button[inset-shadow][raised] > .p-button:not(:disabled):hover{
        }

        p-button[inset-shadow][raised] > .p-button-secondary{
          box-shadow: inset 0 0 10px var(--p-surface-100), var(--p-button-raised-shadow);
        }
        p-button[inset-shadow][raised] > .p-button-white{
          box-shadow: inset 0 0 10px var(--p-surface-500), var(--p-button-raised-shadow);
        }



        .p-button-outlined.p-button-accent:not(:disabled):active {
          background: var(--p-button-outlined-accent-active-background);
          color: var(--p-button-outlined-accent-color);
        }
        .p-button-outlined.p-button-accent:not(:disabled):hover {
          background: var(--p-button-outlined-accent-hover-background);
          color: var(--p-button-outlined-accent-color);
        }

        
        
        .p-button-outlined.p-button-accent-light:not(:disabled):active {
          background: var(--p-button-outlined-accent-light-active-background);
          border-color: var(--p-button-outlined-accent-light-border-color);
          color: var(--p-button-outlined-accent-light-color);
        }
        .p-button-outlined.p-button-accent-light:not(:disabled):hover {
          background: var(--p-button-outlined-accent-light-hover-background);
          border-color: var(--p-button-outlined-accent-light-border-color);
          color: var(--p-button-outlined-accent-light-color);
        }

        .p-button-outlined.p-button-accent-muted:not(:disabled):active {
          background: var(--p-button-outlined-accent-muted-active-background);
          border-color: var(--p-button-outlined-accent-muted-border-color);
          color: var(--p-button-outlined-accent-muted-color);
        }
        .p-button-outlined.p-button-accent-muted:not(:disabled):hover {
          background: var(--p-button-outlined-accent-muted-hover-background);
          border-color: var(--p-button-outlined-accent-muted-border-color);
          color: var(--p-button-outlined-accent-muted-color);
        }

        .p-button-outlined.p-button-aquamarine:not(:disabled):active {
          background: var(--p-button-outlined-aquamarine-active-background);
          border-color: var(--p-button-outlined-aquamarine-border-color);
          color: var(--p-button-outlined-aquamarine-color);
        }
        .p-button-outlined.p-button-aquamarine:not(:disabled):hover {
          background: var(--p-button-outlined-aquamarine-hover-background);
          border-color: var(--p-button-outlined-aquamarine-border-color);
          color: var(--p-button-outlined-aquamarine-color);
        }

        .p-button-outlined.p-button-turquoise:not(:disabled):active {
          background: var(--p-button-outlined-turquoise-active-background);
          border-color: var(--p-button-outlined-turquoise-border-color);
          color: var(--p-button-outlined-turquoise-color);
        }
        .p-button-outlined.p-button-turquoise:not(:disabled):hover {
          background: var(--p-button-outlined-turquoise-hover-background);
          border-color: var(--p-button-outlined-turquoise-border-color);
          color: var(--p-button-outlined-turquoise-color);
        }

        .p-button-outlined.p-button-smalt-blue:not(:disabled):active {
          background: var(--p-button-outlined-smalt-blue-active-background);
          border-color: var(--p-button-outlined-smalt-blue-border-color);
          color: var(--p-button-outlined-smalt-blue-color);
        }
        .p-button-outlined.p-button-smalt-blue:not(:disabled):hover {
          background: var(--p-button-outlined-smalt-blue-hover-background);
          border-color: var(--p-button-outlined-smalt-blue-border-color);
          color: var(--p-button-outlined-smalt-blue-color);
        }

        .p-button-outlined.p-button-white:not(:disabled):active {
          background: var(--p-button-outlined-white-active-background);
          border-color: var(--p-button-outlined-white-border-color);
          color: var(--p-button-outlined-white-color);
        }
        .p-button-outlined.p-button-white:not(:disabled):hover {
          background: var(--p-button-outlined-white-hover-background);
          border-color: var(--p-button-outlined-white-border-color);
          color: var(--p-button-outlined-white-color);
        }

        .p-button-outlined.p-button-accent {
            background:transparent;
            border-color: var(--p-button-outlined-accent-border-color);
            color: var(--p-button-outlined-accent-color);
        }
        .p-button-outlined.p-button-accent-light {
            background:transparent;
            border-color: var(--p-button-outlined-accent-light-border-color);
            color: var(--p-button-outlined-accent-light-color);
        }
        .p-button-outlined.p-button-accent-muted {
            background:transparent;
            border-color: var(--p-button-outlined-accent-muted-border-color);
            color: var(--p-button-outlined-accent-muted-color);
        }

 
        .p-button-outlined.p-button-aquamarine {
            background:transparent;
            border-color: var(--p-button-outlined-aquamarine-border-color);
            color: var(--p-button-outlined-aquamarine-color);
        }

        .p-button-outlined.p-button-turquoise {
            background:transparent;
            border-color: var(--p-button-outlined-turquoise-border-color);
            color: var(--p-button-outlined-turquoise-color);
        }

         .p-button-outlined.p-button-smalt-blue {
            background:transparent;
            border-color: var(--p-button-outlined-smalt-blue-border-color);
            color: var(--p-button-outlined-smalt-blue-color);
        }
            .p-button-outlined.p-button-white {
            background:transparent;
            border-color: var(--p-button-outlined-white-border-color);
            color: var(--p-button-outlined-white-color);
        }


        p-button[inset-shadow] > .p-button-outlined.p-button, .p-button-outlined.p-button-primary {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-primary-border-color);
          color: var(--p-button-outlined-primary-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-accent {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-accent-border-color);
          color: var(--p-button-outlined-accent-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-accent-light {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-accent-light-border-color);
          color: var(--p-button-outlined-accent-light-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-accent-muted {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-accent-muted-border-color);
          color: var(--p-button-outlined-accent-muted-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-aquamarine {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-aquamarine-border-color);
          color: var(--p-button-outlined-aquamarine-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-turquoise {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-turquoise-border-color);
          color: var(--p-button-outlined-turquoise-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-smalt-blue {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-smalt-blue-border-color);
          color: var(--p-button-outlined-smalt-blue-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-secondary {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-secondary-border-color);
          color: var(--p-button-outlined-secondary-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-success {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-success-border-color);
          color: var(--p-button-outlined-success-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-info {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-info-border-color);
          color: var(--p-button-outlined-info-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-warn {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-warn-border-color);
          color: var(--p-button-outlined-warn-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-help {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-help-border-color);
          color: var(--p-button-outlined-help-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-danger {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-danger-border-color);
          color: var(--p-button-outlined-danger-color);
        }
        p-button[inset-shadow] > .p-button-outlined.p-button-contrast {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-contrast-border-color);
          color: var(--p-button-outlined-contrast-color);
        }        
        p-button[inset-shadow] > .p-button-outlined.p-button-white {
          background:transparent;
          box-shadow: inset 0 0 10px var(--p-button-outlined-white-border-color);
          color: var(--p-button-outlined-white-color);
        }


        p-button[raised][inset-shadow] > .p-button-outlined.p-button-primary {
          box-shadow: inset 0 0 10px var(--p-button-outlined-primary-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-accent {
          box-shadow: inset 0 0 10px var(--p-button-outlined-accent-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-accent-light {
          box-shadow: inset 0 0 10px var(--p-button-outlined-accent-light-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-accent-muted {
          box-shadow: inset 0 0 10px var(--p-button-outlined-accent-muted-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-aquamarine {
          box-shadow: inset 0 0 10px var(--p-button-outlined-aquamarine-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-turquoise {
          box-shadow: inset 0 0 10px var(--p-button-outlined-turquoise-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-smalt-blue {
          box-shadow: inset 0 0 10px var(--p-button-outlined-smalt-blue-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-secondary {
          box-shadow: inset 0 0 10px var(--p-button-outlined-secondary-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-success {
          box-shadow: inset 0 0 10px var(--p-button-outlined-success-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-info {
          box-shadow: inset 0 0 10px var(--p-button-outlined-info-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-warn {
          box-shadow: inset 0 0 10px var(--p-button-outlined-warn-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-help {
          box-shadow: inset 0 0 10px var(--p-button-outlined-help-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-danger {
          box-shadow: inset 0 0 10px var(--p-button-outlined-danger-border-color), var(--p-button-raised-shadow);
        }
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-contrast {
          box-shadow: inset 0 0 10px var(--p-button-outlined-contrast-border-color), var(--p-button-raised-shadow);
        }        
        p-button[raised][inset-shadow] > .p-button-outlined.p-button-white {
          box-shadow: inset 0 0 10px var(--p-button-outlined-white-border-color), var(--p-button-raised-shadow);
        }

        p-button[bordered] > .p-button-primary{
          border-color: var(--p-button-primary-hover-background)
        }
        p-button[bordered] > .p-button-accent{
          border-color: var(--p-button-accent-hover-background)
        }
        p-button[bordered] > .p-button-aquamarine{
          border-color: var(--p-button-aquamarine-hover-background)
        }
        p-button[bordered] > .p-button-turquoise{
          border-color: var(--p-button-turquoise-hover-background)
        }
        p-button[bordered] > .p-button-smalt-blue{
          border-color: var(--p-button-smalt-blue-hover-background)
        }
        p-button[bordered] > .p-button-secondary{
          border-color: var(--p-button-secondary-hover-background)
        }
        p-button[bordered] > .p-button-success{
          border-color: var(--p-button-success-hover-background)
        }
        p-button[bordered] > .p-button-info{
          border-color: var(--p-button-info-hover-background)
        }
        p-button[bordered] > .p-button-warn{
          border-color: var(--p-button-warn-hover-background)
        }
        p-button[bordered] > .p-button-help{
          border-color: var(--p-button-help-hover-background)
        }
        p-button[bordered] > .p-button-danger{
          border-color: var(--p-button-danger-hover-background)
        }
        p-button[bordered] > .p-button-contrast{
          border-color: var(--p-button-contrast-hover-background)
        }
        p-button[bordered] > .p-button-white{
          border-color: var(--p-button-white-hover-background)
        }
        p-button[bordered] > .p-button-accent-light{
          border-color: var(--p-button-accent-light-hover-background)
        }

        p-button[bordered] > .p-button-accent-muted{
          border-color: var(--p-button-accent-muted-hover-background)
        }



      `,
      extend: {
        accent: {
          background: '{accent.color}',
          hoverBackground: '{accent.hover.color}',
          activeBackground: '{accent.active.color}',
          borderColor: '{accent.color}',
          hoverBorderColor: '{accent.hover.color}',
          activeBorderColor: '{accent.active.color}',
          color: '{accent.contrast.color}',
          hoverColor: '{accent.contrast.color}',
          activeColor: '{accent.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {elephant.200}',
          },
        },
        'accent-light': {
          background: '{accent-light.color}',
          hoverBackground: '{accent-light.hover.color}',
          activeBackground: '{accent-light.active.color}',
          borderColor: '{accent-light.color}',
          hoverBorderColor: '{accent-light.hover.color}',
          activeBorderColor: '{accent-light.active.color}',
          color: '{accent-light.contrast.color}',
          hoverColor: '{accent-light.contrast.color}',
          activeColor: '{accent-light.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {elephant.200}',
          },
        },
        'accent-muted': {
          background: '{accent-muted.color}',
          hoverBackground: '{accent-muted.hover.color}',
          activeBackground: '{accent-muted.active.color}',
          borderColor: '{accent-muted.color}',
          hoverBorderColor: '{accent-muted.hover.color}',
          activeBorderColor: '{accent-muted.active.color}',
          color: '{accent-muted.contrast.color}',
          hoverColor: '{accent-muted.contrast.color}',
          activeColor: '{accent-muted.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {elephant.200}',
          },
        },
        aquamarine: {
          background: '{aquamarine.color}',
          hoverBackground: '{aquamarine.hover.color}',
          activeBackground: '{aquamarine.active.color}',
          borderColor: '{aquamarine.color}',
          hoverBorderColor: '{aquamarine.hover.color}',
          activeBorderColor: '{aquamarine.active.color}',
          color: '{aquamarine.contrast.color}',
          hoverColor: '{aquamarine.contrast.color}',
          activeColor: '{aquamarine.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {aquamarine.200}',
          },
        },
        turquoise: {
          background: '{turquoise.color}',
          hoverBackground: '{turquoise.hover.color}',
          activeBackground: '{turquoise.active.color}',
          borderColor: '{turquoise.color}',
          hoverBorderColor: '{turquoise.hover.color}',
          activeBorderColor: '{turquoise.active.color}',
          color: '{turquoise.contrast.color}',
          hoverColor: '{turquoise.contrast.color}',
          activeColor: '{turquoise.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {turquoise.200}',
          },
        },
        'smalt-blue': {
          background: '{smalt-blue.color}',
          hoverBackground: '{smalt-blue.hover.color}',
          activeBackground: '{smalt-blue.active.color}',
          borderColor: '{smalt-blue.color}',
          hoverBorderColor: '{smalt-blue.hover.color}',
          activeBorderColor: '{smalt-blue.active.color}',
          color: '{smalt-blue.contrast.color}',
          hoverColor: '{smalt-blue.contrast.color}',
          activeColor: '{smalt-blue.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {smalt-blue.200}',
          },
        },
        white: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          activeBackground: '{surface.200}',
          borderColor: '{surface.0}',
          hoverBorderColor: '{surface.100}',
          activeBorderColor: '{surface.200}',
          color: '{surface.950}',
          hoverColor: '{surface.950}',
          activeColor: '{surface.950}',
          focusRing: {
            color: '{surface.0}',
            shadow: 'none',
          },
        },
        outlined: {
          accent: {
            hoverBackground: '{elephant.50}',
            activeBackground: '{elephant.100}',
            borderColor: '{accent.color}',
            color: '{accent.color}',
          },

          'accent-light': {
            hoverBackground: '{elephant.50}',
            activeBackground: '{elephant.100}',
            borderColor: '{accent.color}',
            color: '{accent.color}',
          },
          'accent-muted': {
            hoverBackground: '{elephant.50}',
            activeBackground: '{elephant.100}',
            borderColor: '{accent.color}',
            color: '{accent.color}',
          },

          aquamarine: {
            hoverBackground: '{aquamarine.50}',
            activeBackground: '{aquamarine.100}',
            borderColor: '{aquamarine.color}',
            color: '{aquamarine.color}',
          },

          turquoise: {
            hoverBackground: '{turquoise.50}',
            activeBackground: '{turquoise.100}',
            borderColor: '{turquoise.color}',
            color: '{turquoise.color}',
          },

          'smalt-blue': {
            hoverBackground: '{smalt-blue.50}',
            activeBackground: '{smalt-blue.100}',
            borderColor: '{smalt-blue.color}',
            color: '{smalt-blue.color}',
          },
          white: {
            hoverBackground: '{surface.800}',
            activeBackground: '{surface.700}',
            borderColor: '{surface.0}',
            color: '{surface.0}',
          },
        },
        text: {
          accent: {
            hoverBackground: '{elephant.50}',
            activeBackground: '{elephant.100}',
            color: '{accent.color}',
          },
          'accent-light': {
            hoverBackground: '{elephant.50}',
            activeBackground: '{elephant.100}',
            color: '{accent-light.color}',
          },
          'accent-muted': {
            hoverBackground: '{elephant.50}',
            activeBackground: '{elephant.100}',
            color: '{accent-muted.color}',
          },
          aquamarine: {
            hoverBackground: '{aquamarine.50}',
            activeBackground: '{aquamarine.100}',
            color: '{aquamarine.color}',
          },

          turquoise: {
            hoverBackground: '{turquoise.50}',
            activeBackground: '{turquoise.100}',
            color: '{turquoise.color}',
          },

          'smalt-blue': {
            hoverBackground: '{smalt-blue.50}',
            activeBackground: '{smalt-blue.100}',
            color: '{smalt-blue.color}',
          },
          white: {
            hoverBackground: '{surface.800}',
            activeBackground: '{surface.700}',
            color: '{surface.0}',
          },
        },
        xxs: {
          fontSize: '{form.field.xxs.font.size}',
          paddingX: '{form.field.xxs.padding.x}',
          paddingY: '{form.field.xxs.padding.y}',
          iconOnlyWidth: '1rem',
        },
        xs: {
          fontSize: '{form.field.xs.font.size}',
          paddingX: '{form.field.xs.padding.x}',
          paddingY: '{form.field.xs.padding.y}',
          iconOnlyWidth: '1.5rem',
        },
        xl: {
          fontSize: '{form.field.xl.font.size}',
          paddingX: '{form.field.xl.padding.x}',
          paddingY: '{form.field.xl.padding.y}',
          iconOnlyWidth: '3.5rem',
        },
        xxl: {
          fontSize: '{form.field.xxl.font.size}',
          paddingX: '{form.field.xxl.padding.x}',
          paddingY: '{form.field.xxl.padding.y}',
          iconOnlyWidth: '4rem',
        },
        xxxl: {
          fontSize: '{form.field.xxxl.font.size}',
          paddingX: '{form.field.xxxl.padding.x}',
          paddingY: '{form.field.xxxl.padding.y}',
          iconOnlyWidth: '5rem',
        },
      },
      root: {
        borderRadius: '{border.radius.xl}',
        raisedShadow:
          '0 5px 8px -2px rgba(0, 0, 0, 0.18), 0 4px 10px 0 rgba(0, 0, 0, 0.12), 0 2px 14px 0 rgba(0, 0, 0, 0.10)',
      },
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: '{surface.100}',
              hoverBackground: '{surface.200}',
              activeBackground: '{surface.300}',
              borderColor: '{surface.100}',
              hoverBorderColor: '{surface.200}',
              activeBorderColor: '{surface.300}',
              color: '{accent.color}',
              hoverColor: '{accent.color}',
              activeColor: '{accent.color}',
              focusRing: {
                color: '{surface.0}',
                shadow: 'none',
              },
            },
          },
          outlined: {
            secondary: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              borderColor: '{surface.300}',
              color: '{surface.700}',
            },

            contrast: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
            },
          },
          text: {
            secondary: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              color: '{surface.700}',
            },

            contrast: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
            },
          },
        },
      },
    },
    tag: {
      css: () => `
        .p-tag {
          text-align: center;
        }
          
        .p-tag-accent  {
          background: var(--p-tag-accent-background);
          color: var(--p-tag-accent-color);
        }
        .p-tag-aquamarine  {
          background: var(--p-tag-aquamarine-background);
          color: var(--p-tag-aquamarine-color);
        } 
        .p-tag-turquoise  {
          background: var(--p-tag-turquoise-background);
          color: var(--p-tag-turquoise-color);
        }  
        .p-tag-smalt-blue  {
          background: var(--p-tag-smalt-blue-background);
          color: var(--p-tag-smalt-blue-color);
        }
      `,
      root: {
        fontWeight: '400',
        roundedBorderRadius: '2rem',
      },
      extend: {
        accent: {
          background: '{elephant.700}',
          color: '{elephant.100}',
        },
        aquamarine: {
          background: '{aquamarine.100}',
          color: '{aquamarine.700}',
        },
        turquoise: {
          background: '{turquoise.100}',
          color: '{turquoise.700}',
        },
        'smalt-blue': {
          background: '{smalt-blue.100}',
          color: '{smalt-blue.700}',
        },
      },
      colorScheme: {
        light: {
          secondary: {
            color: '{surface.950}',
          },
        },
      },
    },
    togglebutton: {
      colorScheme: {
        light: {
          root: {
            checkedColor: '{accent.color}',
          },
          content: {
            checkedBackground: '{primary.color}',
          },
        },
      },
    },
    badge: {
      css: () => `
      .p-badge-accent {
          background: var(--p-badge-accent-background);
          color: var(--p-badge-accent-color);
      }
      .p-badge-aquamarine {
          background: var(--p-badge-aquamarine-background);
          color: var(--p-badge-aquamarine-color);
      }
      .p-badge-turquoise {
          background: var(--p-badge-turquoise-background);
          color: var(--p-badge-turquoise-color);
      }
      .p-badge-smalt-blue {
          background: var(--p-badge-smalt-blue-background);
          color: var(--p-badge-smalt-blue-color);
      }
      `,
      extend: {
        accent: {
          background: '{accent.color}',
          color: '{accent.contrast.color}',
        },
        aquamarine: {
          background: '{aquamarine.color}',
          color: '{aquamarine.contrast.color}',
        },
        turquoise: {
          background: '{turquoise.color}',
          color: '{turquoise.contrast.color}',
        },
        'smalt-blue': {
          background: '{smalt-blue.color}',
          color: '{smalt-blue.contrast.color}',
        },
      },
    },
    avatar: {
      css: () => `

      .p-avatar-xxl {
          width: var(--p-avatar-xxl-width);
          height: var(--p-avatar-xxl-width);
          font-size: var(--p-avatar-xxl-font-size);
      }
      .p-avatar-xxl .p-avatar-icon {
          font-size: var(--p-avatar-xxl-icon-size);
          width: var(--p-avatar-xxl-icon-size);
          height: var(--p-avatar-xxl-icon-size);
      }
            
      .p-avatar:not(.p-avatar-circle) img {
          border-radius: var(--p-avatar-border-radius);
      }
      .p-avatar[severity="accent"] {
          background: var(--p-avatar-accent-background);
          color: var(--p-avatar-accent-color);
      }
      .p-avatar[severity="accent-light"] {
          background: var(--p-avatar-accent-light-background);
          color: var(--p-avatar-accent-light-color);
      }
      .p-avatar[severity="accent-muted"] {
          background: var(--p-avatar-accent-muted-background);
          color: var(--p-avatar-accent-muted-color);
      }
      .p-avatar[severity="secondary"] {
          background: var(--p-avatar-secondary-background);
          color: var(--p-avatar-secondary-color);
      }
      .p-avatar[severity="success"] {
          background: var(--p-avatar-success-background);
          color: var(--p-avatar-success-color);
      }
      .p-avatar[severity="info"] {
          background: var(--p-avatar-info-background);
          color: var(--p-avatar-info-color);
      }
      .p-avatar[severity="warn"]{
          background: var(--p-avatar-warn-background);
          color: var(--p-avatar-warn-color);
      }
      .p-avatar[severity="danger"] {
          background: var(--p-avatar-danger-background);
          color: var(--p-avatar-danger-color);
      }
      .p-avatar[severity="contrast"] {
          background: var(--p-avatar-contrast-background);
          color: var(--p-avatar-contrast-color);
      }
      .p-avatar[severity="aquamarine"] {
          background: var(--p-avatar-aquamarine-background);
          color: var(--p-avatar-aquamarine-color);
      }
      .p-avatar[severity="turquoise"] {
          background: var(--p-avatar-turquoise-background);
          color: var(--p-avatar-turquoise-color);
      }
      .p-avatar[severity="smalt-blue"] {
          background: var(--p-avatar-smalt-blue-background);
          color: var(--p-avatar-smalt-blue-color);
      }
          
      .p-avatar[outlined]{
          background: transparent;
          border: 1px solid var(--p-avatar-primary-background);
          color: var(--p-avatar-primary-background);
      }
      .p-avatar[severity="primary"][outlined]{
          border: 1px solid var(--p-avatar-primary-background);
          color: var(--p-avatar-primary-background);
      }
      .p-avatar[severity="accent"][outlined]{
          border: 1px solid var(--p-avatar-accent-background);
          color: var(--p-avatar-accent-background);
      }
      .p-avatar[severity="accent-light"][outlined]{
          border: 1px solid var(--p-avatar-accent-light-background);
          color: var(--p-avatar-accent-light-background);
      }
      .p-avatar[severity="accent-muted"][outlined]{
          border: 1px solid var(--p-avatar-accent-muted-background);
          color: var(--p-avatar-accent-muted-background);
      }
      .p-avatar[severity="secondary"][outlined] {
          border: 1px solid var(--p-avatar-secondary-background);
          color: var(--p-avatar-secondary-background);
      }
      .p-avatar[severity="success"][outlined] {
          border: 1px solid var(--p-avatar-success-background);
          color: var(--p-avatar-success-background);
      }
      .p-avatar[severity="info"][outlined] {
          border: 1px solid var(--p-avatar-info-background);
          color: var(--p-avatar-info-background);
      }
      .p-avatar[severity="warn"][outlined] {
          border: 1px solid var(--p-avatar-warn-background);
          color: var(--p-avatar-warn-background);
      }
      .p-avatar[severity="danger"][outlined]{
          border: 1px solid var(--p-avatar-danger-background);
          color: var(--p-avatar-danger-background);
      }
      .p-avatar[severity="contrast"][outlined] {
          border: 1px solid var(--p-avatar-contrast-background);
          color: var(--p-avatar-contrast-background);
      }
      .p-avatar[severity="aquamarine"][outlined] {
          border: 1px solid var(--p-avatar-aquamarine-background);
          color: var(--p-avatar-aquamarine-background);
      }
      .p-avatar[severity="turquoise"][outlined] {
          border: 1px solid var(--p-avatar-turquoise-background);
          color: var(--p-avatar-turquoise-background);
      }
      .p-avatar[severity="smalt-blue"][outlined] {
          border: 1px solid var(--p-avatar-smalt-blue-background);
          color: var(--p-avatar-smalt-blue-background);
      }

      .p-avatar[opaque] {
          background: color-mix(in srgb, var(--p-avatar-primary-background), transparent 50%);
          color: var(--p-avatar-primary-color);
      }
      .p-avatar[severity="primary"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-primary-background), transparent 50%);
          color: var(--p-avatar-primary-color);
      }
      .p-avatar[severity="accent"][opaque]{
          background: color-mix(in srgb, var(--p-avatar-accent-background), transparent 50%);          
          color: var(--p-avatar-accent-color);
      }
      .p-avatar[severity="accent-light"][opaque]{
          background: color-mix(in srgb, var(--p-avatar-accent-light-background), transparent 50%);          
          color: var(--p-avatar-accent-light-color);
      }
      .p-avatar[severity="accent-muted"][opaque]{
          background: color-mix(in srgb, var(--p-avatar-accent-muted-background), transparent 50%);          
          color: var(--p-avatar-accent-muted-color);
      }
      .p-avatar[severity="secondary"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-secondary-background), transparent 50%);
          color: var(--p-avatar-secondary-color);
      }
      .p-avatar[severity="success"][opaque]{
          background: color-mix(in srgb, var(--p-avatar-success-background), transparent 50%);
          color: var(--p-avatar-success-color);
      }
      .p-avatar[severity="info"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-info-background), transparent 50%);
          color: var(--p-avatar-info-color);
      }
      .p-avatar[severity="warn"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-warn-background), transparent 50%);
          color: var(--p-avatar-warn-color);
      }
      .p-avatar[severity="danger"][opaque]{
          background: color-mix(in srgb, var(--p-avatar-danger-background), transparent 50%);
          color: var(--p-avatar-danger-color);
      }
      .p-avatar[severity="contrast"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-contrast-background), transparent 50%);
          color: var(--p-avatar-contrast-color);
      }
      .p-avatar[severity="aquamarine"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-aquamarine-background), transparent 50%);
        color: var(--p-avatar-aquamarine-color);
      }
      .p-avatar[severity="turquoise"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-turquoise-background), transparent 50%);
        color: var(--p-avatar-turquoise-color);
      }
      .p-avatar[severity="smalt-blue"][opaque] {
          background: color-mix(in srgb, var(--p-avatar-smalt-blue-background), transparent 50%);
        color: var(--p-avatar-smalt-blue-color);
      }


      .p-avatar[bordered]{
          border: 1px solid var(--p-avatar-primary-hover-background);
      }
      .p-avatar[severity="primary"][bordered]{
          border: 1px solid var(--p-avatar-primary-hover-background);
      }
      .p-avatar[severity="accent"][bordered]{
          border: 1px solid var(--p-avatar-accent-hover-background);
      }
      .p-avatar[severity="accent-light"][bordered]{
          border: 1px solid var(--p-avatar-accent-light-hover-background);
      }
      .p-avatar[severity="accent-muted"][bordered]{
          border: 1px solid var(--p-avatar-accent-muted-hover-background);
      }
      .p-avatar[severity="secondary"][bordered] {
          border: 1px solid var(--p-avatar-secondary-hover-background);
      }
      .p-avatar[severity="success"][bordered] {
          border: 1px solid var(--p-avatar-success-hover-background);
      }
      .p-avatar[severity="info"][bordered] {
          border: 1px solid var(--p-avatar-info-hover-background);
        }
      .p-avatar[severity="warn"][bordered] {
          border: 1px solid var(--p-avatar-warn-hover-background);
      }
      .p-avatar[severity="danger"][bordered]{
          border: 1px solid var(--p-avatar-danger-hover-background);
      }
      .p-avatar[severity="contrast"][bordered] {
          border: 1px solid var(--p-avatar-contrast-hover-background);
      }
      .p-avatar[severity="aquamarine"][bordered] {
          border: 1px solid var(--p-avatar-aquamarine-hover-background);
      }
      .p-avatar[severity="turquoise"][bordered] {
          border: 1px solid var(--p-avatar-turquoise-hover-background);
      }
      .p-avatar[severity="smalt-blue"][bordered] {
          border: 1px solid var(--p-avatar-smalt-blue-hover-background);
      }

      `,
      extend: {
        xxl: {
          width: '5rem',
          height: '5rem',
          fontSize: '2.5rem',
          icon: {
            size: '2.5rem',
          },
          group: {
            offset: '-2.25rem',
          },
        },
        primary: {
          background: '{primary.color}',
          color: '{primary.contrast.color}',
          hoverBackground: '{primary.hover.color}',
        },
        accent: {
          background: '{accent.color}',
          color: '{accent.contrast.color}',
          hoverBackground: '{accent.hover.color}',
        },
        'accent-light': {
          background: '{accent-light.color}',
          color: '{accent-light.contrast.color}',
          hoverBackground: '{accent-light.hover.color}',
        },
        'accent-muted': {
          background: '{accent-muted.color}',
          color: '{accent-muted.contrast.color}',
          hoverBackground: '{accent-muted.hover.color}',
        },
        aquamarine: {
          background: '{aquamarine.color}',
          color: '{aquamarine.contrast.color}',
          hoverBackground: '{aquamarine.hover.color}',
        },
        turquoise: {
          background: '{turquoise.color}',
          color: '{turquoise.contrast.color}',
          hoverBackground: '{turquoise.hover.color}',
        },
        'smalt-blue': {
          background: '{smalt-blue.color}',
          color: '{smalt-blue.contrast.color}',
          hoverBackground: '{smalt-blue.hover.color}',
        },
        secondary: {
          background: '{surface.100}',
          color: '{accent.color}',
          hoverBackground: '{surface.200}',
        },
        success: {
          background: '{green.500}',
          color: '{surface.0}',
          hoverBackground: '{green.600}',
        },
        info: {
          background: '{sky.500}',
          color: '{surface.0}',
          hoverBackground: '{sky.600}',
        },
        warn: {
          background: '{orange.500}',
          color: '{surface.0}',
          hoverBackground: '{orange.600}',
        },
        danger: {
          background: '{red.500}',
          color: '{surface.0}',
          hoverBackground: '{red.700}',
        },
        contrast: {
          background: '{surface.950}',
          color: '{surface.0}',
          hoverBackground: '{surface.800}',
        },
      },
      root: {
        background: '{primary.color}',
        color: '{primary.contrast.color}',
      },
    },
    progressspinner: {
      colorScheme: {
        light: {
          root: {
            colorOne: '{primary.color}',
            colorTwo: '{accent.color}',
            colorThree: '{acquamrine.color}',
            colorFour: '{smalt-blue.color}',
          },
        },
        dark: {
          root: {
            colorOne: '{primary.color}',
            colorTwo: '{accent.color}',
            colorThree: '{acquamrine.color}',
            colorFour: '{smalt-blue.color}',
          },
        },
      },
    },
    card: {
      css: () => `
      .p-card.no-padding .p-card-body {
        padding: 0 !important;
      }
      p-card[severity="primary"].p-card{
        background-color: var(--p-card-primary-background);
        color: var(--p-card-primary-color);
      }
      p-card[severity="accent"].p-card{
        background-color: var(--p-card-accent-background);
        color: var(--p-card-accent-color);
      }
      p-card[severity="aquamarine"].p-card{
        background-color: var(--p-card-aquamarine-background);
        color: var(--p-card-aquamarine-color);
      }
      p-card[severity="turquoise"].p-card{
        background-color: var(--p-card-turquoise-background);
        color: var(--p-card-turquoise-color);
      }
      p-card[severity="smalt-blue"].p-card{
        background-color: var(--p-card-smalt-blue-background);
        color: var(--p-card-smalt-blue-color);
      }
      p-card[severity="contrast"].p-card{
        background-color: var(--p-card-contrast-background);
        color: var(--p-card-contrast-color);
      }
      p-card[inset-shadow].p-card {
        box-shadow: inset 0 0 10px var(--p-surface-300) !important;
      }
      p-card[opacified].p-card {
        background-color: color-mix(in srgb, var(--p-surface-100) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }
      p-card[severity="primary"][opacified].p-card {
        background-color: color-mix(in srgb, var(--p-card-primary-background) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }
      p-card[severity="accent"][opacified].p-card {
        background-color: color-mix(in srgb, var(--p-card-accent-background) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }
      p-card[severity="aquamarine"][opacified].p-card {
        background-color: color-mix(in srgb, var(--p-card-aquamarine-background) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }
      p-card[severity="turquoise"][opacified].p-card {
        background-color: color-mix(in srgb, var(--p-card-turquoise-background) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }
      p-card[severity="smalt-blue"][opacified].p-card {
        background-color: color-mix(in srgb, var(--p-card-smalt-blue-background) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }
      p-card[severity="contrast"][opacified] > .p-card {
        background-color: color-mix(in srgb, var(--p--card-contrast-background) 35%, transparent) !important;
        backdrop-filter: blur(2px) !important;
      }        
      `,
      extend: {
        primary: {
          background: '{primary.color}',
          color: '{primary.contrast.color}',
        },
        accent: {
          background: '{accent.color}',
          color: '{accent.contrast.color}',
        },
        aquamarine: {
          background: '{aquamarine.color}',
          color: '{aquamarine.contrast.color}',
        },
        turquoise: {
          background: '{turquoise.color}',
          color: '{turquoise.contrast.color}',
        },
        'smalt-blue': {
          background: '{smalt-blue.color}',
          color: '{smalt-blue.contrast.color}',
        },
        contrast: {
          background: '{surface.950}',
          color: '{surface.0}',
        },
        radial: {
          background: '{surface.950}',
          color: '{surface.0}',
        },
      },
    },
    carousel: {
      css: () => `
        .p-carousel .p-carousel-content-container .p-carousel-indicator-list .p-carousel-indicator button {
            background-color: var(--p-surface-700);
            width: 1.5rem;
            height: 0.5rem;
            transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
            border-radius: var(--p-card-border-radius);
        }
        .p-carousel .p-carousel-content-container .p-carousel-indicator-list .p-carousel-indicator.p-carousel-indicator-active button {
          background-color: var(--p-surface-0) !important;
          width: 1.5rem;
          height: 0.5rem;
          transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
          border-radius: var(--p-card-border-radius);
        }

        .p-carousel .p-carousel-content-container .p-carousel-content .p-carousel-prev-button button {
          background-color: transparent !important;
          color: var(--p-surface-0) !important;
        }
        .p-carousel .p-carousel-content-container .p-carousel-content .p-carousel-next-button button {
          background-color: transparent !important;
          color: var(--p-surface-0) !important;
        }
      `,
    },
    // divider: {
    //   root: {
    //     borderColor: '{surface.0}',
    //   },
    //   content: {
    //     color: '{primary.color}',
    //   },
    // },
    dialog: {
      css: () => `
        .p-dialog-header {
          border: none;
          border-bottom: 1px solid var(--p-surface-500);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          background-color: color-mix(in oklab, var(--p-surface-0) 75%, transparent);
          backdrop-filter: blur(2px) !important;
          z-index: 50;
          border-top-left-radius: var(--p-dialog-border-radius) !important;
          border-top-right-radius: var(--p-dialog-border-radius) !important;
        }
        .p-dialog-footer {
          border: none;
          border-top: 1px solid var(--p-surface-500);
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: color-mix(in oklab, var(--p-surface-0) 75%, transparent);
          backdrop-filter: blur(2px) !important;
          z-index: 50;
          border-bottom-left-radius: var(--p-dialog-border-radius);
          border-bottom-right-radius: var(--p-dialog-border-radius);
        }

        .p-dialog-content {
          height: 100%;
          border-radius: var(--p-dialog-border-radius) !important;
        }

      `,
      content: {
        padding: 'o',
      },
      footer: {
        padding: '{overlay.modal.padding}',
      },
    },
    scrollpanel: {
      colorScheme: {
        light: {
          bar: {
            background: '{surface.700}',
          },
        },
      },
    },
    drawer: {
      css: () => `
        .p-drawer-header {
          border: none;
          border-bottom: 1px solid var(--p-surface-500);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          background-color: color-mix(in oklab, var(--p-surface-0) 75%, transparent);
          backdrop-filter: blur(2px) !important;
          z-index: 50;
        }
        .p-drawer-footer {
          border: none;
          border-top: 1px solid var(--p-surface-500);
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: color-mix(in oklab, var(--p-surface-0) 75%, transparent);
          backdrop-filter: blur(2px) !important;
          z-index: 50;
        }
      `,
      root: {
        borderColor: 'none',
      },
      content: {
        padding: '0',
      },
    },
    listbox: {
      css: () => `
        .p-listbox-header > div {
          width: 100%;
        }
      `,
      root: {
        shadow: 'none',
        borderColor: 'transparent',
      },
      list: {
        gap: '4',
        header: {
          padding: '0.75rem 1.5rem',
        },
      },
      option: {
        padding: '0.75rem 1.5rem',
      },
      optionGroup: {
        padding: '0.75rem 1.5rem',
        background: '{surface.400}',
        color: '{surface.0}',
      },
    },
    menubar: {
      css: () => `
        
      `,
      root: {
        background: 'transparent',
        borderColor: 'transparent',
        borderRadius: '{border.radius.none}',
      },
    },
    tabs: {
      css: () => `
        .p-tab-active {
            border-width: 0 0 3px 0;
        }
        p-tabs[severity="smalt-blue"] {
          .p-tablist-tab-list, .p-tablist-nav-button, .p-tabpanels {
            background: var(--p-smalt-blue-color);
          }
          .p-tablist-tab-list .p-tab.p-tab-active {
            color: var(--p-surface-100) !important;
            border-color: var(--p-surface-100) !important;
          }
          .p-tablist-tab-list .p-tab:not(.p-tab-active) {
            color: var(--p-surface-300);
          }
          .p-tablist-nav-button {
            box-shadow: 0 0 10px 50px color-mix(in oklab, var(--p-smalt-blue-color) 75%, transparent);
          }
        }
      `,
    },
    tooltip: {
      css: () => `
        p-tooltip {
          background-color: var(--p-elephant-900) !important;
          backdrop-filter: blur(2px) !important;
        }
      `,
    },
    panelmenu: {
      root: {
        gap: '0',
      },
      panel: {
        first: {
          borderWidth: '0',
        },
        last: {
          borderWidth: '0',
        },
        borderWidth: '0',
        padding: '0',
      },
    },
  },
  css: () => `

  
    .bg-accent {
      background-color: var(--p-accent-color)!important;
      color: var(--p-surface-0)!important;
    }

    .bg-aquamarine {
      background-color: var(--p-aquamarine-color)!important;
      color: var(--p-aquamarine-contrast-color)!important;
    }

    .bg-smalt-blue {
      background-color: var(--p-smalt-blue-color)!important;
      color: var(--p-smalt-blue-contrast-color)!important;
    }

    .bg-turquoise {
      background-color: var(--p-turquoise-color)!important;
      color: var(--p-turquoise-contrast-color)!important;
    }


    .bg-accent-100 {
      background-color: var(--p-elephant-100)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-accent-200 {
      background-color: var(--p-elephant-200)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-accent-300 {
      background-color: var(--p-elephant-300)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-accent-400 {
      background-color: var(--p-elephant-400)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-accent-500 {
      background-color: var(--p-elephant-500)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-accent-600 {
      background-color: var(--p-elephant-600)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-accent-700 {
      background-color: var(--p-elephant-700)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-accent-800 {
      background-color: var(--p-elephant-800)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-accent-900 {
      background-color: var(--p-elephant-900)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-accent-950 {
      background-color: var(--p-elephant-950)!important;
      color: var(--p-surface-0)!important;
    }

    .bg-turquoise-100 {
      background-color: var(--p-turquoise-100)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-turquoise-200 {
      background-color: var(--p-turquoise-200)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-turquoise-300 {
      background-color: var(--p-turquoise-300)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-turquoise-400 {
      background-color: var(--p-turquoise-400)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-turquoise-500 {
      background-color: var(--p-turquoise-500)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-turquoise-600 {
      background-color: var(--p-turquoise-600)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-turquoise-700 {
      background-color: var(--p-turquoise-700)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-turquoise-800 {
      background-color: var(--p-turquoise-800)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-turquoise-900 {
      background-color: var(--p-turquoise-900)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-turquoise-950 {
      background-color: var(--p-turquoise-950)!important;
      color: var(--p-surface-0)!important;
    }

    .bg-aquamarine-100 {
      background-color: var(--p-aquamarine-100)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-aquamarine-200 {
      background-color: var(--p-aquamarine-200)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-aquamarine-300 {
      background-color: var(--p-aquamarine-300)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-aquamarine-400 {
      background-color: var(--p-aquamarine-400)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-aquamarine-500 {
      background-color: var(--p-aquamarine-500)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-aquamarine-600 {
      background-color: var(--p-aquamarine-600)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-aquamarine-700 {
      background-color: var(--p-aquamarine-700)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-aquamarine-800 {
      background-color: var(--p-aquamarine-800)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-aquamarine-900 {
      background-color: var(--p-aquamarine-900)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-aquamarine-950 {
      background-color: var(--p-aquamarine-950)!important;
      color: var(--p-surface-0)!important;
    }

    .bg-smalt-blue-100 {
      background-color: var(--p-smalt-blue-100)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-smalt-blue-200 {
      background-color: var(--p-smalt-blue-200)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-smalt-blue-300 {
      background-color: var(--p-smalt-blue-300)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-smalt-blue-400 {
      background-color: var(--p-smalt-blue-400)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-smalt-blue-500 {
      background-color: var(--p-smalt-blue-500)!important;
      color: var(--p-surface-950)!important;
    }
    .bg-smalt-blue-600 {
      background-color: var(--p-smalt-blue-600)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-smalt-blue-700 {
      background-color: var(--p-smalt-blue-700)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-smalt-blue-800 {
      background-color: var(--p-smalt-blue-800)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-smalt-blue-900 {
      background-color: var(--p-smalt-blue-900)!important;
      color: var(--p-surface-0)!important;
    }
    .bg-smalt-blue-950 {
      background-color: var(--p-smalt-blue-950)!important;
      color: var(--p-surface-0)!important;
    }


    .bg-primary-600 {
      color: var(--p-surface-0)!important;
    }
    .bg-primary-700 {
      color: var(--p-surface-0)!important;
    }
    .bg-primary-800 {
      color: var(--p-surface-0)!important;
    }
    .bg-primary-900 {
      color: var(--p-surface-0)!important;
    }
    .bg-primary-950 {
      color: var(--p-surface-0)!important;
    }

    
    .bg-surface-600 {
      color: var(--p-surface-0)!important;
    }
    .bg-surface-700 {
      color: var(--p-surface-0)!important;
    }
    .bg-surface-800 {
      color: var(--p-surface-0)!important;
    }
    .bg-surface-900 {
      color: var(--p-surface-0)!important;
    }
    .bg-surface-950 {
      color: var(--p-surface-0)!important;
    }


    .text-accent {
      color: var(--p-accent-color);
    }

    .text-aquamarine {
      color: var(--p-aquamarine-color);
    }

    .text-turquoise {
      color: var(--p-turquoise-color);
    }

    .text-smalt-blue {
      color: var(--p-smalt-blue-color);
    }
  `,
});
